import {
  Children,
  Fragment,
  isValidElement,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import FormContext from "react-bootstrap/FormContext";
import Select from "react-select";
function labelText(node) {
  return Children.toArray(node)
    .map((n) => (isValidElement(n) ? labelText(n.props.children) : String(n)))
    .join("");
}
function optionsFrom(children, disabled = false) {
  return Children.toArray(children).flatMap((node) => {
    if (!isValidElement(node)) return [];
    if (node.type === Fragment)
      return optionsFrom(node.props.children, disabled);
    if (node.type === "optgroup")
      return [
        {
          label: node.props.label,
          options: optionsFrom(
            node.props.children,
            disabled || node.props.disabled,
          ),
        },
      ];
    if (node.type !== "option") return [];
    const label = labelText(node.props.children);
    return [
      {
        value: String(node.props.value ?? label),
        label,
        isDisabled: disabled || node.props.disabled,
      },
    ];
  });
}
/** Medispace's searchable, portalled dropdown, adapted to Claimsdesk's native
 * select API. Keep a native select for real change events, required validation
 * and FormData; existing form handlers and numeric/string values stay intact. */
const ThemedSelect = forwardRef(function ThemedSelect(
  {
    children,
    value,
    defaultValue,
    onChange,
    size,
    htmlSize,
    menuMinWidth = 220,
    maxMenuHeight = 300,
    compactMenu = false,
    hideSelectedOptions,
    // Pass null in dropdown editors to keep options inside their click boundary.
    menuPortalTarget = typeof document === "undefined" ? undefined : document.body,
    className = "",
    style,
    id,
    disabled,
    required,
    name,
    isInvalid,
    isValid,
    "data-hint": hint,
    "data-hint-placement": hintPlacement,
    ...props
  },
  forwardedRef,
) {
  const { controlId } = useContext(FormContext);
  const generatedId = useId();
  const inputId = id || controlId || `cd-select-${generatedId}`;
  const native = useRef(null);
  const select = useRef(null);
  const options = optionsFrom(children);
  const flatOptions = options.flatMap((option) => option.options || [option]);
  useImperativeHandle(forwardedRef, () => native.current);
  const [internal, setInternal] = useState(
    String(defaultValue ?? flatOptions[0]?.value ?? ""),
  );
  const [invalid, setInvalid] = useState(false);
  const invalidState = isInvalid || invalid || /\bis-invalid\b/.test(className);
  const validState = isValid || /\bis-valid\b/.test(className);
  const current = String(value ?? internal);
  const small = size === "sm" || className.includes("form-select-sm");
  const large = size === "lg" || className.includes("form-select-lg");
  const [label, setLabel] = useState();
  const refreshLabel = () => {
    const associated = Array.from(document.getElementsByTagName("label")).find(
      (item) => item.htmlFor === inputId,
    );
    const container = native.current?.parentElement?.parentElement;
    setLabel(
      associated?.textContent ||
        container?.querySelector("label")?.textContent ||
        hint || props.title,
    );
  };
  useEffect(() => {
    refreshLabel();
  }, [inputId, hint, props.title]);
  useEffect(() => {
    const form = native.current?.form;
    const reset = () => {
      setInvalid(false);
      if (value === undefined)
        setInternal(String(defaultValue ?? flatOptions[0]?.value ?? ""));
    };
    form?.addEventListener("reset", reset);
    return () => form?.removeEventListener("reset", reset);
  }, [value, defaultValue, flatOptions[0]?.value]);
  const styles = {
    container: (base) => ({ ...base, width: "100%", minWidth: 0 }),
    control: (base, state) => ({
      ...base,
      minWidth: 0,
      minHeight: small
        ? "2rem"
        : large
          ? "3rem"
          : "var(--ui-control-height, 2.625rem)",
      backgroundColor: disabled
        ? "var(--bs-secondary-bg)"
        : "var(--bs-body-bg)",
      borderColor:
        invalidState
          ? "var(--bs-danger)"
          : state.isFocused
            ? "var(--bs-primary)"
            : validState
              ? "var(--bs-success)"
              : "var(--bs-border-color)",
      borderRadius: "var(--bs-border-radius)",
      boxShadow: state.isFocused
        ? "0 0 0 3px rgba(var(--bs-primary-rgb), .18)"
        : "none",
      fontSize: style?.fontSize ?? (small ? ".875rem" : "inherit"),
      lineHeight: 1.5,
      "&:hover": { borderColor: invalidState ? "var(--bs-danger)" : validState ? "var(--bs-success)" : "var(--bs-primary)" },
    }),
    valueContainer: (base) => ({
      ...base,
      minWidth: 0,
      padding: small ? "0 6px" : "0 11px",
    }),
    singleValue: (base) => ({
      ...base,
      color: disabled ? "var(--bs-secondary-color)" : "var(--bs-body-color)",
      margin: 0,
    }),
    placeholder: (base) => ({
      ...base,
      color: "var(--bs-secondary-color)",
      margin: 0,
    }),
    input: (base) => ({
      ...base,
      color: "var(--bs-body-color)",
      margin: 0,
      padding: 0,
    }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (base) => ({
      ...base,
      color: "var(--bs-secondary-color)",
      padding: small ? 4 : 8,
    }),
    menuPortal: (base) => {
      const viewport = window.innerWidth,
        width = Math.min(Math.max(Number(base.width) || 0, menuMinWidth), viewport - 24);
      return {
        ...base,
        width,
        left: Math.max(
          12,
          Math.min(Number(base.left) || 0, viewport - width - 12),
        ),
        zIndex: 1100,
      };
    },
    menu: (base) => ({
      ...base,
      color: "var(--bs-body-color)",
      backgroundColor: "var(--bs-body-bg)",
      border: "1px solid var(--bs-border-color)",
      borderRadius: "var(--bs-border-radius-lg)",
      boxShadow: "var(--ui-menu-shadow, 0 12px 32px rgb(0 0 0 / .14))",
      overflow: "hidden",
    }),
    groupHeading: (base) => ({
      ...base,
      color: "var(--bs-secondary-color)",
      fontWeight: 600,
      ...(compactMenu ? { marginBottom: 3 } : {}),
    }),
    menuList: (base) => ({ ...base, padding: 5 }),
    option: (base, state) => ({
      ...base,
      padding: compactMenu ? "4px 10px" : "8px 10px",
      borderRadius: "var(--ui-option-radius, 5px)",
      overflowWrap: "anywhere",
      color: state.isDisabled
        ? "var(--bs-secondary-color)"
        : state.isSelected
          ? "var(--cd-selection-ink, #fff)"
          : "var(--bs-body-color)",
      backgroundColor: state.isSelected
        ? "var(--bs-primary)"
        : state.isFocused
          ? "var(--bs-tertiary-bg)"
          : "transparent",
      cursor: state.isDisabled ? "not-allowed" : "pointer",
      ":active": {
        backgroundColor: "var(--bs-primary)",
        color: "var(--cd-selection-ink, #fff)",
      },
    }),
  };
  // Lists and multi-selects retain native semantics; the searchable replacement
  // is intentionally a single-choice control, like Medispace's original.
  if (props.multiple || htmlSize || typeof size === "number")
    return (
      <select
        {...props}
        data-hint={hint}
        data-hint-placement={hintPlacement}
        ref={native}
        id={id || controlId}
        name={name}
        disabled={disabled}
        required={required}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        size={htmlSize || (typeof size === "number" ? size : undefined)}
        className={className || "form-select"}
        style={style}
      >
        {children}
      </select>
    );
  return (
    <div
      data-hint={hint}
      data-hint-placement={hintPlacement}
      className={`cd-select ${className.replace(/\bform-select(?:-sm|-lg)?\b/g, "")}`}
      style={style}
    >
      <select
        {...props}
        ref={native}
        id={`${inputId}-native`}
        name={name}
        disabled={disabled}
        required={required}
        value={current}
        aria-hidden="true"
        tabIndex={-1}
        className="cd-select-native"
        onFocus={() => select.current?.focus()}
        onInvalid={(event) => {
          event.preventDefault();
          setInvalid(true);
          select.current?.focus();
          props.onInvalid?.(event);
        }}
        onChange={(event) => {
          setInvalid(false);
          onChange?.(event);
          // DropdownText deliberately clears the native value after filling its
          // adjacent free-text field. Reflect that reset in the visible control.
          setInternal(event.target.value);
        }}
      >
        {children}
      </select>
      <Select
        ref={select}
        inputId={inputId}
        instanceId={inputId}
        aria-label={props["aria-label"] || label}
        aria-labelledby={props["aria-labelledby"]}
        aria-describedby={props["aria-describedby"]}
        aria-required={required}
        aria-invalid={invalidState}
        options={options}
        value={flatOptions.find((o) => o.value === current) ?? null}
        isDisabled={disabled}
        onBlur={() =>
          native.current?.dispatchEvent(
            new FocusEvent("focusout", { bubbles: true }),
          )
        }
        onKeyDown={props.onKeyDown}
        tabIndex={props.tabIndex}
        autoFocus={props.autoFocus}
        placeholder={props.placeholder || "Select…"}
        noOptionsMessage={() => "No matches"}
        classNamePrefix="cd-choice"
        onMenuOpen={() => {
          // Older Claimsdesk fields have a visual label without htmlFor.
          refreshLabel();
        }}
        onChange={(option) => {
          if (!native.current) return;
          native.current.value = option?.value ?? "";
          native.current.dispatchEvent(new Event("change", { bubbles: true }));
        }}
        menuPortalTarget={menuPortalTarget}
        menuPosition={menuPortalTarget === null ? "absolute" : "fixed"}
        menuPlacement="auto"
        menuShouldScrollIntoView={false}
        maxMenuHeight={maxMenuHeight}
        hideSelectedOptions={hideSelectedOptions}
        styles={styles}
      />
    </div>
  );
});
export default ThemedSelect;
